import { useState } from "react";
import { CreateUserDTO, UserRoles } from "../../types/user.types";
import { UsersForm } from "./users-form";
import { useAuth } from "../../context/AuthContext";
import { TeamProvider } from "../../context/TeamContext";
import { useNavigate } from "react-router-dom";
import { UsersAPI } from "../../APIs/users.api";
import { useToast } from "../../components/toast";
import { useErrorHandler } from "../../hooks/useErrorHandler";
import { NewCredentialsDisplay } from "../../components/new-credentials-display";

export const UsersAdd = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { user } = useAuth();
  const { addToast } = useToast();
  const navigate = useNavigate();
  const [newUserCredentials, setNewUserCredentials] = useState<{
    username: string;
    password: string;
  } | null>(null);
  const currentUserRoleId = user?.roleId;
  const [usernameApiError, setUsernameApiError] = useState<string | null>(null);
  const { handleError, clearError, error } = useErrorHandler();

  const [userData, setUserData] = useState<CreateUserDTO>({
    username: "",
    role_id: undefined,
    team_id: undefined,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setUsernameApiError(null);
    clearError();
    setIsSubmitting(true);

    if (requireTeam && !userData.team_id) {
      return;
    }

    try {
      const response = await UsersAPI.createUser(userData);
      if (response.statusCode === 201) {
        setNewUserCredentials({
          username: response.data.username,
          password: response.data.password,
        });
        addToast({
          message: "¡Usuario creado!",
          type: "success",
          location: "bottom-center",
        });
      } else {
        if (response.additionalData?.errorCode === "USERNAME_IN_USE") {
          setUsernameApiError(response.message);
        }

        handleError(response.message, { persist: true });
      }
    } catch (error) {
      handleError("Error al crear el usuario, intente nuevamente", {
        persist: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  const TEAM_REQUIRED_ROLES = new Set([
    UserRoles.PRIMARY_USER,
    UserRoles.INVITED_USER,
  ]);
  const requireTeam = userData.role_id
    ? TEAM_REQUIRED_ROLES.has(userData.role_id)
    : false;

  const handleChange = (e: React.ChangeEvent<HTMLElement>) => {
    if (error) clearError();
    setUsernameApiError(null);

    const { name, value } = e.target as HTMLInputElement | HTMLSelectElement;
    const updatedValue =
      name === "role_id" || name === "team_id" ? Number(value) : value;

    setUserData((prev) => {
      const newFormData = {
        ...prev,
        [name]: updatedValue,
      };

      if (
        name === "role_id" &&
        !TEAM_REQUIRED_ROLES.has(updatedValue as UserRoles)
      ) {
        newFormData.team_id = undefined;
      }

      return newFormData;
    });
  };

  const handleCancel = () => {
    navigate("/dashboard/users");
  };

  const handleBack = () => {
    navigate("/dashboard/users");
  };

  return (
    <div className="max-w-2xl mx-auto p-6">
      <TeamProvider>
        {newUserCredentials ? (
          <NewCredentialsDisplay
            username={newUserCredentials.username}
            password={newUserCredentials.password}
            onBack={handleBack}
          />
        ) : (
          <UsersForm
            mode="create"
            requireTeam={requireTeam}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            onChange={handleChange}
            isSubmitting={isSubmitting}
            currentUserRole={currentUserRoleId!}
            usernameApiError={usernameApiError}
            formData={userData}
            error={error}
          />
        )}
      </TeamProvider>
    </div>
  );
};
